import React, { useState } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, Tabs, Table, Tag, Space, Statistic, Progress, Descriptions } from 'antd';
import { 
  CreditCardOutlined, 
  HistoryOutlined, 
  CheckCircleOutlined,
  DownloadOutlined,
  RocketOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const BillingPlans = () => {
  const [activeTab, setActiveTab] = useState('subscription');

  // Payment history data
  const [paymentHistory] = useState([
    { id: 1, date: '2024-12-01', amount: 299, status: 'paid', invoice: 'INV-2024-001', method: 'Visa ****4242', plan: 'Professional' },
    { id: 2, date: '2024-11-01', amount: 299, status: 'paid', invoice: 'INV-2024-002', method: 'Visa ****4242', plan: 'Professional' },
    { id: 3, date: '2024-10-01', amount: 299, status: 'paid', invoice: 'INV-2024-003', method: 'Visa ****4242', plan: 'Professional' },
    { id: 4, date: '2024-09-01', amount: 299, status: 'paid', invoice: 'INV-2024-004', method: 'Visa ****4242', plan: 'Professional' },
    { id: 5, date: '2024-08-01', amount: 149, status: 'paid', invoice: 'INV-2024-005', method: 'Visa ****4242', plan: 'Starter' },
  ]);

  // Payment history columns
  const paymentColumns = [
    { 
      title: 'Date', 
      dataIndex: 'date', 
      key: 'date',
      render: (text) => <Text>{text}</Text>
    },
    { 
      title: 'Invoice', 
      dataIndex: 'invoice', 
      key: 'invoice',
      render: (text) => <Text strong>{text}</Text>
    },
    { 
      title: 'Plan', 
      dataIndex: 'plan', 
      key: 'plan',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    { 
      title: 'Amount', 
      dataIndex: 'amount', 
      key: 'amount',
      render: (amount) => <Text strong>${amount}</Text>
    },
    { 
      title: 'Payment Method', 
      dataIndex: 'method', 
      key: 'method'
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => (
        <Tag color={status === 'paid' ? 'green' : status === 'pending' ? 'orange' : 'red'} icon={status === 'paid' ? <CheckCircleOutlined /> : null}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Button size="small" icon={<DownloadOutlined />}>
          Download
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'subscription',
      label: (
        <span>
          <CreditCardOutlined /> Current Subscription
        </span>
      ),
      children: (
        <div>
          {/* Current Plan */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Space direction="vertical" size="small">
                  <Text type="secondary">Current Plan</Text>
                  <Title level={2} style={{ margin: 0 }}>Professional Plan</Title>
                  <Text type="secondary">Billed monthly</Text>
                </Space>
              </Col>
              <Col>
                <Button type="primary" size="large" icon={<RocketOutlined />}>
                  Upgrade Plan
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Plan Details */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Monthly Cost"
                  value={299}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Next Billing Date"
                  value="Jan 1, 2025"
                  valueStyle={{ fontSize: 20 }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Account Status"
                  value="Active"
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Usage Statistics */}
          <Card title="Usage This Month" style={{ marginBottom: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>Active Campaigns</Text>
                  <Text strong>12 / 50</Text>
                </div>
                <Progress percent={24} strokeColor="#1890ff" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>Ad Spend</Text>
                  <Text strong>$8,500 / $25,000</Text>
                </div>
                <Progress percent={34} strokeColor="#722ed1" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>Team Members</Text>
                  <Text strong>5 / 10</Text>
                </div>
                <Progress percent={50} strokeColor="#52c41a" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>API Calls</Text>
                  <Text strong>45,000 / 100,000</Text>
                </div>
                <Progress percent={45} strokeColor="#fa8c16" />
              </div>
            </Space>
          </Card>

          {/* Plan Features */}
          <Card title="Plan Features">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Active Campaigns">
                <Tag color="blue">Up to 50 campaigns</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Monthly Ad Spend">
                <Tag color="purple">Up to $25,000</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Team Members">
                <Tag color="green">Up to 10 users</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="API Access">
                <Tag color="orange">100,000 calls/month</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Support">
                <Tag color="blue">Priority Email & Chat</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Analytics">
                <Tag color="green">Advanced Reports</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Integrations">
                <Tag color="purple">All Platforms</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Payment Method */}
          <Card title="Payment Method" style={{ marginTop: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Space>
                  <CreditCardOutlined style={{ fontSize: 24 }} />
                  <div>
                    <Text strong>Visa ending in 4242</Text>
                    <br />
                    <Text type="secondary">Expires 12/2025</Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Button>Update Payment Method</Button>
              </Col>
            </Row>
          </Card>
        </div>
      )
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined /> Payment History
        </span>
      ),
      children: (
        <div>
          {/* Summary */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Total Paid"
                  value={1345}
                  prefix="$"
                  valueStyle={{ color: '#52c41a' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Lifetime payments
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="This Year"
                  value={1196}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  2024 payments
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Invoices"
                  value={5}
                  valueStyle={{ color: '#722ed1' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Total invoices
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Payment History Table */}
          <Card title="Transaction History">
            <Table
              columns={paymentColumns}
              dataSource={paymentHistory}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Billing & Plans' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Billing & Plans</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Manage your subscription and view payment history
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        type="card"
      />
    </div>
  );
};

export default BillingPlans;
